// @core/stores/task/TaskStatusDefinitionStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { taskApi } from '@core/api';
import type {
  CreateStatusDefinitionRequest,
  TaskStatusDefinition,
  UpdateStatusDefinitionRequest,
} from '@core/types/task';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class TaskStatusDefinitionStore {
  // List state
  items: TaskStatusDefinition[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: TaskStatusDefinition | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, TaskStatusDefinition>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): TaskStatusDefinition | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: TaskStatusDefinition | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create status definition
   */
  async CreateStatusDefinition(data: CreateStatusDefinitionRequest): Promise<TaskStatusDefinition> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await taskApi.CreateStatusDefinition(data);
      const item = response.definition;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete status definition
   */
  async DeleteStatusDefinition(status_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await taskApi.DeleteStatusDefinition(status_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== status_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === status_id) {
          this.currentItem = null;
        }

        this.removeFromCache(status_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * List status definitions
   */
  async ListStatusDefinitions(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await taskApi.ListStatusDefinitions(params);

      runInAction(() => {
        this.items = response.definitions?.definitions ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update status definition
   */
  async UpdateStatusDefinition(status_id: string, data: UpdateStatusDefinitionRequest): Promise<TaskStatusDefinition> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await taskApi.UpdateStatusDefinition(status_id, data);
      const item = response.definition;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const taskStatusDefinitionStore = new TaskStatusDefinitionStore();
